import { RiCloseLine } from '@remixicon/react';
import { createPortal } from 'react-dom';
import { AgButton, AgText } from 'src/components/ui';
import { useSidebar } from 'src/providers/SidebarContextProvider';

import type { GridBulkActionsState } from '../selection';

type Props = {
  /** Everything selection-derived — see `useGridBulkActions`. */
  bulk: GridBulkActionsState;
  bulkActions: React.ReactNode;
  renderSelectionAdornment?: (selectedRows: Record<string, unknown>) => React.ReactNode;
};

export default function BulkActionToolbar({ bulk, bulkActions, renderSelectionAdornment }: Props) {
  const { selectedRows, selectedCount, entityName, entityNamePlural, clearSelection } = bulk;
  const { sidebarOpen } = useSidebar();

  const sidebarWidth = sidebarOpen ? 256 : 65;

  return createPortal(
    <div
      role="toolbar"
      aria-label={`${entityNamePlural} bulk actions`}
      className="fixed top-25 right-0 z-1200 mx-auto flex flex-col items-start gap-2 rounded-md bg-[#333] p-2 pl-5 text-white"
      style={{
        left: `${sidebarWidth}px`,
        maxWidth: `calc(100% - ${sidebarWidth}px - 48px)`,
        boxShadow: '0 18px 24px -5px rgba(20, 21, 26, 0.10), 0 8px 8px -5px rgba(20, 21, 26, 0.05)',
      }}
    >
      <div className="flex w-full items-center gap-2">
        <div className="flex shrink-0 items-center gap-2">
          <AgText size="16" color="white" className="whitespace-nowrap">
            {selectedCount} {selectedCount === 1 ? entityName : entityNamePlural} selected
          </AgText>
          {renderSelectionAdornment?.(selectedRows)}
        </div>
        <AgButton variant="ghost-inverted" onPress={clearSelection}>
          <RiCloseLine size={20} /> Deselect All
        </AgButton>
        <div className="grow" />
        {bulkActions}
      </div>
    </div>,
    document.body
  );
}
